import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { action, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { cpfBorrowerFill, hireNext, inssBorrowerFill, offersSimulationFill } from './simulation-hire.fill';
import { summaryContextText, type SummaryAgentState } from './simulation-summary.context';
import {
  INSS_PAYROLL_PATH,
  PRIVATE_CDC_PAYROLL_PATH,
  PRIVATE_PAYROLL_PATH,
  SIMULATION_READ,
} from './simulation.screen';

// Manifestos do consignado INSS, do privado e do privado CDC (U23, U42), arquivo leve para o `src/app/screens.ts`. O
// caminho é o do legado com o rótulo padrão (`formatRoute('Consignado INSS' | 'Consignado privado' | 'Consignado
// privado CDC')`, labelConfig.ts:11-45): estável, e o rótulo do tenant só no texto. Herdam `CreditNote:Read` de
// `/ccb` e só existem com `ENABLE_NEW_SIMULATION === 1`. A simulação das ofertas pode consultar a margem paga na
// Dataprev ou no INSS (X01) e só roda pelo clique da pessoa; o Luy prepara o Tomador e a Simulação (U56).

/** O Luy nas jornadas de ofertas (U56): tomador, simulação e avançar, sem simular. */
function offersFills(prefix: string, inss: boolean) {
  return {
    borrower: inss ? inssBorrowerFill(prefix) : cpfBorrowerFill(prefix),
    simulation: offersSimulationFill(prefix),
    next: hireNext(prefix),
  };
}
export const inssPayrollFills = offersFills('simulation.inssPayrollNew', true);
export const privatePayrollFills = offersFills('simulation.privatePayrollNew', false);
export const privateCdcPayrollFills = offersFills('simulation.privateCdcPayroll', false);

export { INSS_PAYROLL_PATH, PRIVATE_CDC_PAYROLL_PATH, PRIVATE_PAYROLL_PATH };
export const INSS_PAYROLL_NEW_PATH = `${INSS_PAYROLL_PATH}/novo`;
export const PRIVATE_PAYROLL_NEW_PATH = `${PRIVATE_PAYROLL_PATH}/novo`;

const NEW_SIMULATION = { flag: { key: 'ENABLE_NEW_SIMULATION', equals: 1 } } as const;

export const simulationInssPayrollNew = action({
  id: 'simulation.inssPayroll.new',
  kind: 'navigate',
  label: msg`Novo`,
  description: msg`Abre a contratação de um consignado INSS novo: os dados do beneficiário, a simulação das ofertas e a escolha da oferta.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(INSS_PAYROLL_NEW_PATH),
});

export const simulationInssPayrollScreen = defineScreen({
  id: 'simulation.inssPayroll',
  path: INSS_PAYROLL_PATH,
  title: msg`Consignado INSS`,
  description: msg`Escolha da jornada de consignado INSS: Novo abre aqui; Refinanciamento e Portabilidade ainda não estão disponíveis.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: [simulationInssPayrollNew],
});

export const simulationInssPayrollNewScreen = defineScreen({
  id: 'simulation.inssPayrollNew',
  path: INSS_PAYROLL_NEW_PATH,
  title: msg`Novo consignado INSS`,
  description: msg`Contratação de consignado INSS em passos: o CPF, o benefício, o telefone e o nome do beneficiário, a simulação das ofertas e a escolha da oferta de crédito. No Resumo, a conferência do titular e da conta e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.inssPayroll', flow: true },
  actions: Object.values(inssPayrollFills),
});

export const simulationPrivatePayrollNew = action({
  id: 'simulation.privatePayroll.new',
  kind: 'navigate',
  label: msg`Novo`,
  description: msg`Abre a contratação de um consignado privado novo (crédito do trabalhador): o CPF, a simulação das ofertas e a escolha da oferta.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PRIVATE_PAYROLL_NEW_PATH),
});

export const simulationPrivatePayrollScreen = defineScreen({
  id: 'simulation.privatePayroll',
  path: PRIVATE_PAYROLL_PATH,
  title: msg`Consignado privado`,
  description: msg`Escolha da jornada de consignado privado: Novo abre aqui; Refinanciamento, quando aparece, abre a contratação por refinanciamento.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: [simulationPrivatePayrollNew],
});

export const simulationPrivatePayrollNewScreen = defineScreen({
  id: 'simulation.privatePayrollNew',
  path: PRIVATE_PAYROLL_NEW_PATH,
  title: msg`Novo consignado privado`,
  description: msg`Contratação de consignado privado (crédito do trabalhador) em passos: o CPF, a simulação das ofertas dos vínculos do trabalhador e a escolha da oferta de crédito. No Resumo, a conferência do titular e da conta e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.privatePayroll', flow: true },
  actions: Object.values(privatePayrollFills),
});

export const simulationPrivateCdcPayrollScreen = defineScreen({
  id: 'simulation.privateCdcPayroll',
  path: PRIVATE_CDC_PAYROLL_PATH,
  title: msg`Consignado privado CDC`,
  description: msg`Contratação de consignado privado CDC em passos: o CPF, a simulação das ofertas CDC dos vínculos do trabalhador e a escolha da oferta de crédito. No Resumo, a conferência do titular e da conta e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: Object.values(privateCdcPayrollFills),
});

/** Contexto do hub do INSS ou do privado; o Refinanciamento do privado só entra quando o cartão está à vista. */
/** Contexto do hub do INSS ou do privado (U46): a etapa à vista, o produto já escolhido e a modalidade marcada. */
export function offersHubContext(
  kind: 'inss' | 'private',
  i18n: I18n,
  refinancing: boolean,
  chosen?: string | null,
): ScreenContext {
  const modality = chosen ?? '';
  const journeys =
    kind === 'inss'
      ? i18n._(
          msg`Escolha da jornada de consignado INSS. Novo abre aqui; Refinanciamento e Portabilidade estão desabilitados.`,
        )
      : refinancing
        ? i18n._(
            msg`Escolha da jornada de consignado privado. Novo abre aqui; Refinanciamento abre a contratação por refinanciamento.`,
          )
        : i18n._(msg`Escolha da jornada de consignado privado. Novo abre aqui.`);
  return {
    summary: [
      kind === 'inss'
        ? i18n._(msg`Contratar consignado INSS, etapa 2: Modalidade. O produto já foi escolhido.`)
        : i18n._(msg`Contratar consignado privado, etapa 2: Modalidade. O produto já foi escolhido.`),
      journeys,
      modality ? i18n._(msg`Modalidade marcada: ${modality}.`) : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { step: 'modalidade', modalityChosen: modality !== '' },
    sensitivity: 'internal',
  };
}

export interface OffersFlowContextInput {
  readonly kind: 'inss' | 'private' | 'privateCdc';
  readonly step: string;
  readonly stepTitle: string;
  readonly offers: number | null;
  readonly restrictions: number;
  readonly selected: boolean;
  /** Só no Resumo: se a pessoa e a conta estão prontas para o envio. */
  readonly summary?: SummaryAgentState | null;
}

/** O que o agente lê do fluxo (U24, U46): a jornada, o passo e as ofertas. Sem CPF, benefício, telefone nem margem. */
export function offersFlowContext(input: OffersFlowContextInput, i18n: I18n): ScreenContext {
  const step = input.stepTitle;
  const offers = input.offers;
  const restrictions = input.restrictions;
  const journey =
    input.kind === 'inss'
      ? i18n._(msg`Contratação de consignado INSS.`)
      : input.kind === 'privateCdc'
        ? i18n._(msg`Contratação de consignado privado CDC.`)
        : i18n._(msg`Contratação de consignado privado.`);
  const parts = [
    journey,
    i18n._(msg`Passo atual: ${step}.`),
    offers === null
      ? ''
      : offers > 0
        ? i18n._(msg`Há ${offers} ofertas para escolher.`)
        : i18n._(msg`Não há ofertas.`),
    offers === 0 && restrictions > 0
      ? i18n._(msg`Há ${restrictions} restrições nos vínculos do CPF.`)
      : '',
    input.selected ? i18n._(msg`Uma oferta está escolhida.`) : '',
    summaryContextText(input.summary, i18n),
  ];
  return {
    summary: parts.filter(Boolean).join(' '),
    fields: {
      journey: input.kind,
      step: input.step,
      offers,
      restrictions,
      selected: input.selected,
      ...(input.summary ? { personFound: input.summary.personFound, bankAccount: input.summary.bankAccount } : {}),
    },
    sensitivity: 'internal',
  };
}
